import axios from 'axios';
// import { RootState } from '../app/store'; // To potentially access token from Redux state

// Use Create React App's way of accessing environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Interceptor to add the auth token and session token to requests
// This assumes you store your token in Redux state under `auth.token`
apiClient.interceptors.request.use(
  (config) => {
    // Example: Get token from localStorage or Redux store
    // const token = localStorage.getItem('authToken');
    // Or, if using Redux:
    // const state: RootState = store.getState(); // You'd need to import your store here
    // const token = state.auth.token;

    const token = localStorage.getItem('approverAuthToken'); // Using localStorage for simplicity here
    const sessionToken = localStorage.getItem('approverSessionToken'); // Session token for tracking

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (sessionToken) {
      config.headers['X-Session-Token'] = sessionToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 (Unauthenticated) errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 (Unauthenticated) and we haven't already tried to handle it
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Clear all auth tokens from localStorage
      localStorage.removeItem('approverAuthToken');
      localStorage.removeItem('approverSessionToken');
      localStorage.removeItem('approverUser');

      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }

      // Return a rejected promise to prevent further processing
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Example of API functions (to be expanded)

/**
 * Login a user.
 * You will need to create a login route in your Laravel api.php if not already present,
 * e.g., Route::post('/login', [AuthController::class, 'login']);
 * This route should return a token on successful login.
 */
export const login = async (credentials: { email: string; password: string }) => {
  // Laravel Sanctum typically uses a CSRF cookie for SPA authentication.
  // For token-based auth, ensure your Laravel backend issues a token.
  // First, get CSRF cookie if using Sanctum SPA auth with cookies
  // await axios.get(process.env.REACT_APP_SANCTUM_CSRF_URL || 'http://localhost:8001/sanctum/csrf-cookie');

  // Then make the login request
  const response = await apiClient.post('/login', credentials); // Adjust '/login' if your route is different
  if (response.data.token) {
    localStorage.setItem('approverAuthToken', response.data.token);
    // Store session token for activity tracking
    if (response.data.session_token) {
      localStorage.setItem('approverSessionToken', response.data.session_token);
    }
    // You might also want to store user data
    if (response.data.user) {
        localStorage.setItem('approverUser', JSON.stringify(response.data.user));
    }
  }
  return response.data; // Contains token, session_token and possibly user data
};

export const logout = async (sessionToken?: string | null) => {
  try {
    // Attempt to invalidate the token on the server side.
    // The auth interceptor will add the current token to this request.
    const payload: any = {};
    if (sessionToken) {
      payload.session_token = sessionToken;
    }
    await apiClient.post('/logout', payload);
    // No specific response data is usually needed from /logout,
    // but you can add error handling if the server call fails.
  } catch (error) {
    // Log the error or handle it as needed, but proceed with local cleanup.
    console.error('API logout failed, proceeding with local cleanup:', error);
  }
  // Always remove local token and user data regardless of API call success.
  localStorage.removeItem('approverAuthToken');
  localStorage.removeItem('approverSessionToken');
  localStorage.removeItem('approverUser');
  // Optionally, you might want to clear other related local storage items or caches.
};

/**
 * Update session activity (heartbeat)
 */
export const updateActivity = async (sessionToken: string) => {
  try {
    await apiClient.post('/activity', { session_token: sessionToken });
  } catch (error) {
    console.error('Failed to update activity:', error);
    // Don't throw error as this is a background operation
  }
};

/**
 * Check if user is already authenticated in main Laravel app
 */
export const checkAuth = async () => {
  try {
    const response = await apiClient.get('/check-auth');

    // If user is authenticated, save the tokens and user data
    if (response.data.authenticated && response.data.token) {
      localStorage.setItem('approverAuthToken', response.data.token);

      if (response.data.session_token) {
        localStorage.setItem('approverSessionToken', response.data.session_token);
      }

      if (response.data.user) {
        localStorage.setItem('approverUser', JSON.stringify(response.data.user));
      }
    }

    return response.data; // Contains { authenticated: boolean, token?, user?, session_token?, message }
  } catch (error) {
    console.error('Check auth failed:', error);
    // Clear any existing tokens if check fails
    localStorage.removeItem('approverAuthToken');
    localStorage.removeItem('approverSessionToken');
    localStorage.removeItem('approverUser');
    throw error;
  }
};

// --- Impersonation API Calls ---

export interface ImpersonationUser {
  id: number;
  name: string;
  email: string;
}

export interface ImpersonationState {
  impersonating: boolean;
  user?: ImpersonationUser;
  impersonated_user?: ImpersonationUser;
  original_user?: ImpersonationUser;
}

/**
 * Get list of users available for impersonation
 */
export const getImpersonationUsers = async (): Promise<ImpersonationUser[]> => {
  const response = await apiClient.get('/impersonate/users');
  return response.data.users;
};

/**
 * Get current impersonation status
 */
export const getImpersonationStatus = async (): Promise<ImpersonationState> => {
  const response = await apiClient.get('/impersonate/status');
  return response.data;
};

/**
 * Get current impersonation state
 */
export const getCurrentImpersonation = async (): Promise<ImpersonationState> => {
  const response = await apiClient.get('/impersonate/current');
  return response.data;
};

/**
 * Start impersonating a user
 */
export const startImpersonation = async (userId: number): Promise<ImpersonationState> => {
  const response = await apiClient.post('/impersonate/start', { user_id: userId });
  return {
    impersonating: true,
    user: response.data.user,
  };
};

/**
 * Stop impersonating
 */
export const stopImpersonation = async (): Promise<void> => {
  await apiClient.post('/impersonate/stop');
};

// --- Requisition API Calls ---

interface FetchRequisitionsParams {
  status?: 'pending_my_approval' | 'pending_all' | 'all_from_my_stores';
  date_from?: string;
  date_to?: string;
  page?: number;
  // Add other potential filter params here
}

export const fetchRequisitions = async (params: FetchRequisitionsParams) => {
  const response = await apiClient.get('/requisitions', { params });
  return response.data; // Expected to be paginated data
};

export const fetchRequisitionById = async (id: number | string) => {
  const response = await apiClient.get(`/requisitions/${id}`);
  return response.data;
};

export const approveRequisitionApi = async (id: number | string) => {
  const response = await apiClient.post(`/approver/requisitions/${id}/approve`);
  return response.data;
};

interface RejectRequisitionPayload {
  comment?: string;
}

export const rejectRequisitionApi = async (id: number | string, payload: RejectRequisitionPayload) => {
  const response = await apiClient.post(`/approver/requisitions/${id}/reject`, payload);
  return response.data;
};

// --- Dashboard API Calls ---
export interface DashboardStats {
  pendingMyApprovalCount: number;
  recentlyApprovedCount: number;
  pendingMyApprovalValue: number; // Assuming this is a numerical value
}

export const fetchDashboardStatsApi = async (): Promise<DashboardStats> => {
  const response = await apiClient.get('/approver/dashboard-stats');
  return response.data;
};

// --- Filter Options API Calls ---

// Interface for store API response
export interface StoreApiResponse {
  id: number;
  name: string;
  storenumber?: string;
  area_id?: number;
  regional_id?: number;
}

// Interface for category API response
export interface CategoryApiResponse {
  id: number;
  name: string;
}

// Interface for area coach API response
export interface AreaCoachApiResponse {
  id: number;
  name: string;
  user_role_id: number;
}

// Fetch stores for dropdown filter
export const fetchStoresApi = async (): Promise<StoreApiResponse[]> => {
  const response = await apiClient.get('/requisitions/stores');
  return response.data.data; // Extract the actual array from the nested data property
};

// Fetch categories for dropdown filter
export const fetchCategoriesApi = async (): Promise<CategoryApiResponse[]> => {
  const response = await apiClient.get('/requisitions/categories');
  return response.data.data; // Extract the actual array from the nested data property
};

// Fetch area coaches for dropdown filter
export const fetchAreaCoachesApi = async (): Promise<AreaCoachApiResponse[]> => {
  const response = await apiClient.get('/requisitions/area-coaches');
  
  // Check if response.data is an array directly (no nested data property)
  if (Array.isArray(response.data)) {
    return response.data;
  }
  
  // Otherwise, try to extract from data property if it exists
  return Array.isArray(response.data.data) ? response.data.data : [];
};

// --- Maintenance Log API Calls ---

export interface MaintenanceLogItem {
  id: number;
  date: string;
  status_id: number;
  internal: number;
  stores: Array<{ id: number; name: string; storenumber?: string }>;
  category: { id: number; name: string } | null;
  subcategory: { id: number; name: string } | null;
  supplier: { id: number; name: string } | null;
  technician: { id: number; name: string } | null;
  order: {
    id: number;
    order_number: string | null;
    reference_number: string | null;
    jobcards?: Array<{
      id: number;
      job_number: string;
      attachment: string;
      po_request_id: number;
    }>;
  } | null;
  assets: Array<{ id: number; completed: boolean; asset: string }>;
  equipmentRequests: Array<{
    id: number;
    completed: boolean;
    equipment: {
      id: number;
      description: string;
      serial_number: string;
      category: string;
      subcategory: string;
    };
  }>;
  user: { id: number; name: string } | null;
  createdBy: { id: number; name: string } | null;
  created_at: string;
  equipmentItems?: Array<{
    asset: string;
    equipment: {
      serial_number: string;
      category: string;
      subcategory: string;
    };
    descriptions: string[];
  }>;
  assetDescriptionPairs?: Array<{
    asset: string;
    descriptions: string[];
  }>;
}

export interface MaintenanceLogFilters {
  search?: string;
  date_from?: string;
  date_to?: string;
  selected_stores?: number[];
  selected_suppliers?: number[];
  category?: string;
  sub_category?: string;
  status?: number;
  selected_regional?: string;
  selected_area_coach?: string;
}

export interface MaintenanceLogResponse {
  data: MaintenanceLogItem[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

export const fetchMaintenanceLogApi = async (
  filters: MaintenanceLogFilters = {},
  page: number = 1
): Promise<MaintenanceLogResponse> => {
  const params = {
    ...filters,
    page,
    per_page: 20,
  };
  const response = await apiClient.get('/maintenance-log', { params });
  return response.data;
};

export interface RegionalCoachApiResponse {
  id: number;
  name: string;
  user_role_id: number;
}

export const fetchRegionalCoachesApi = async (): Promise<RegionalCoachApiResponse[]> => {
  const response = await apiClient.get('/maintenance-log/regional-coaches');
  return Array.isArray(response.data.data) ? response.data.data : response.data;
};

export const fetchAreaCoachesForMaintenanceApi = async (
  selectedRegional?: string
): Promise<AreaCoachApiResponse[]> => {
  const params = selectedRegional ? { selected_regional: selectedRegional } : {};
  const response = await apiClient.get('/maintenance-log/area-coaches', { params });
  return Array.isArray(response.data.data) ? response.data.data : response.data;
};

export const exportMaintenanceLogApi = async (filters: MaintenanceLogFilters = {}): Promise<void> => {
  const response = await apiClient.post('/maintenance-log/export', filters, {
    responseType: 'blob',
  });
  
  // Create a blob from the response
  const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  
  // Create a link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = `maintenance_log_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`;
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// --- Profile API Calls ---

export interface ProfileUpdatePayload {
  name: string;
  email: string;
}

export interface ProfileUpdateResponse {
  message: string;
  status: string;
  user: {
    id: number;
    name: string;
    email: string;
    user_role_id: number;
    is_admin: boolean;
  };
}

export interface PasswordUpdatePayload {
  current_password: string;
  password: string;
  password_confirmation: string;
}

export interface PasswordUpdateResponse {
  message: string;
  status: string;
}

export const updateProfileApi = async (payload: ProfileUpdatePayload): Promise<ProfileUpdateResponse> => {
  const response = await apiClient.patch('/profile', payload);
  return response.data;
};

export const updatePasswordApi = async (payload: PasswordUpdatePayload): Promise<PasswordUpdateResponse> => {
  const response = await apiClient.put('/password', payload);
  return response.data;
};

// ==================== Purchase Orders ====================

export interface PoListParams {
  search?: string;
  status_id?: number;
  stores?: number[];
  date?: string;
  internal?: number;
  supplier_id?: number;
  technician_id?: number;
  linked_only?: boolean;
  date_from?: string;
  date_to?: string;
  required_grn?: boolean;
  required_dn_jbc?: boolean;
  multi_store?: boolean;
  view_pending?: boolean;
  view_submitted?: boolean;
  view_tba?: boolean;
  view_approved?: boolean;
  view_rejected?: boolean;
  view_cancelled?: boolean;
  per_page?: number;
  page?: number;
}

export const fetchPurchaseOrdersApi = async (params: PoListParams): Promise<any> => {
  const response = await apiClient.get('/purchase-orders', { params });
  return response.data;
};

export const fetchPurchaseOrderApi = async (id: number): Promise<any> => {
  const response = await apiClient.get(`/purchase-orders/${id}`);
  return response.data;
};

export const fetchPoStatsApi = async (): Promise<any> => {
  const response = await apiClient.get('/purchase-orders/stats');
  return response.data;
};

export const fetchPoFiltersDataApi = async (): Promise<any> => {
  const response = await apiClient.get('/purchase-orders/filters/data');
  return response.data;
};

export const approvePoApi = async (id: number): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${id}/approve`);
  return response.data;
};

export const rejectPoApi = async (id: number, reason: string): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${id}/reject`, { reason });
  return response.data;
};

export const rejectAtrApi = async (id: number, comment: string): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${id}/reject-atr`, { comment });
  return response.data;
};

export const submitPoApi = async (id: number): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${id}/submit`);
  return response.data;
};

export const unsubmitPoApi = async (id: number): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${id}/unsubmit`);
  return response.data;
};

export const cancelPoApi = async (id: number, reason?: string): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${id}/cancel`, { reason });
  return response.data;
};

export const deletePoApi = async (id: number): Promise<any> => {
  const response = await apiClient.delete(`/purchase-orders/${id}`);
  return response.data;
};

export const fulfillPoApi = async (id: number, fulfillmentId: number): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${id}/fulfill`, { fulfillment_id: fulfillmentId });
  return response.data;
};

export const splitPoApi = async (id: number): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${id}/split`);
  return response.data;
};

export const mergePoApi = async (mainOrderId: number, orderIds: number[]): Promise<any> => {
  const response = await apiClient.post('/purchase-orders/merge', { main_order_id: mainOrderId, order_ids: orderIds });
  return response.data;
};

export const bulkApprovePoApi = async (orderIds: number[]): Promise<any> => {
  const response = await apiClient.post('/purchase-orders/bulk-approve', { order_ids: orderIds });
  return response.data;
};

export const bulkRejectPoApi = async (orderIds: number[], reason: string): Promise<any> => {
  const response = await apiClient.post('/purchase-orders/bulk-reject', { order_ids: orderIds, reason });
  return response.data;
};

// ---- PO Document Management ----

export const fetchPoDocumentsApi = async (poId: number): Promise<any> => {
  const response = await apiClient.get(`/purchase-orders/${poId}/documents`);
  return response.data;
};

export const uploadQuotationApi = async (poId: number, formData: FormData): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${poId}/documents/quotation`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadInvoiceApi = async (poId: number, formData: FormData): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${poId}/documents/invoice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadDeliveryNoteApi = async (poId: number, formData: FormData): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${poId}/documents/delivery-note`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadJobcardApi = async (poId: number, formData: FormData): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${poId}/documents/jobcard`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadTicketApi = async (poId: number, formData: FormData): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${poId}/documents/ticket`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadOtherDocApi = async (poId: number, formData: FormData): Promise<any> => {
  const response = await apiClient.post(`/purchase-orders/${poId}/documents/other`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deletePoDocumentApi = async (poId: number, type: string, documentId: number): Promise<any> => {
  const response = await apiClient.delete(`/purchase-orders/${poId}/documents/${type}/${documentId}`);
  return response.data;
};

export const getPoDocumentFileUrlApi = async (poId: number, type: string, documentId: number): Promise<any> => {
  const response = await apiClient.get(`/purchase-orders/${poId}/documents/${type}/${documentId}/file`);
  return response.data;
};

export default apiClient;
